import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import PdfModalPdfjs from "./PdfModalPdfjs";
import { getFunctions, httpsCallable } from "firebase/functions";
import "./WrongPanel.css";

const SESSIONS = ["1교시", "2교시", "3교시", "4교시"];

// ✅ 정식 과목 명칭 매핑
const FORMAL_SUBJECT_MAPPING = {
  "간": "간계내과학",
  "심": "심계내과학",
  "비": "비계내과학",
  "폐": "폐계내과학",
  "신": "신계내과학",
  "상한": "상한론",
  "사상": "사상의학",
  "침구": "침구의학",
  "법규": "보건의약관계법규",
  "외과": "외과학",
  "신정": "신경정신과학",
  "안이비": "안이비인후과학",
  "부인": "부인과학",
  "소아": "소아과학",
  "예방": "예방의학",
  "생리": "한방생리학",
  "본초": "본초학",
  "기타": "기타 과목",
  "내과학1": "내과학1",
};

// ✅ 표시 순서
const SUBJECT_ORDER = [
  "간", "심", "비", "폐", "신",
  "상한", "사상", "침구", "법규",
  "외과", "신정", "안이비", "부인",
  "소아", "예방", "생리", "본초",
  "내과학1",
];

// -----------------------------
// ✅ 공통 템플릿 (1차~8차 동일 기본 구성)
// -----------------------------
const ROUND1_SESSION1_ARRAY = [
  "신","신","폐","심","심","간","폐","폐","폐","간",
  "비","폐","신","신","신","간","비","비","비","비",
  "심","심","심","심","간","비","비","심","심","심",
  "신","신","심","폐","심","비","비","비","비","비",
  "비","폐","폐","폐","폐","간","신","간","신","간",
  "간","간","폐","신","간","심","심","심","심","심",
  "폐","폐","폐","폐","비","비","비","비","간","간",
  "간","간","간","신","신","신","신","신","신","간"
];

const TEMPLATE_SESSION2 = [
  ..."상한 ".repeat(16).trim().split(" "),
  ..."사상 ".repeat(16).trim().split(" "),
  ..."침구 ".repeat(48).trim().split(" "),
  ..."법규 ".repeat(20).trim().split(" "),
];

const TEMPLATE_SESSION3 = [
  ..."외과 ".repeat(16).trim().split(" "),
  ..."신정 ".repeat(16).trim().split(" "),
  ..."안이비 ".repeat(16).trim().split(" "),
  ..."부인 ".repeat(32).trim().split(" "),
];

const TEMPLATE_SESSION4 = [
  ..."소아 ".repeat(24).trim().split(" "),
  ..."예방 ".repeat(24).trim().split(" "),
  ..."생리 ".repeat(16).trim().split(" "),
  ..."본초 ".repeat(16).trim().split(" "),
];

// ✅ 회차별 매핑 (1~8차 동일 템플릿)
const SUBJECT_MAPPINGS = (() => {
  const rounds = ["1차","2차","3차","4차","5차","6차","7차","8차"];
  const obj = {};
  rounds.forEach((r) => {
    obj[r] = {
      "1교시": ROUND1_SESSION1_ARRAY, // UI는 단일 "내과학1"로 묶지만 원과목 추적용
      "2교시": TEMPLATE_SESSION2,
      "3교시": TEMPLATE_SESSION3,
      "4교시": TEMPLATE_SESSION4,
    };
  });
  return obj;
})();

// ✅ UI용 과목 매핑: 1교시는 무조건 "내과학1"
function getSubjectByQuestion(qNum, session, roundLabel) {
  if (session === "1교시") return "내과학1";
  const mapping = SUBJECT_MAPPINGS[roundLabel]?.[session];
  if (mapping && qNum >= 1 && qNum <= mapping.length) return mapping[qNum - 1];
  return "기타";
}

// ✅ 모달 제목용: 1교시라도 실제 과목(간/심/비/폐/신)을 찾아 정식명칭으로 표시
function getFormalForModal(session, qNum, roundLabel) {
  if (session === "1교시") {
    const raw = SUBJECT_MAPPINGS[roundLabel]?.["1교시"]?.[qNum - 1];
    return FORMAL_SUBJECT_MAPPING[raw] || FORMAL_SUBJECT_MAPPING["내과학1"] || "내과학1";
  }
  const subj = getSubjectByQuestion(qNum, session, roundLabel);
  return FORMAL_SUBJECT_MAPPING[subj] || subj || "";
}

// ✅ 세션/회차 존재 확인
function isSessionAvailable(roundLabel, session) {
  return !!(SUBJECT_MAPPINGS[roundLabel]?.[session]);
}
function isRoundAvailable(roundLabel) {
  return !!(SUBJECT_MAPPINGS[roundLabel]);
}

export default function ControversialPanel({
  examYears,
  examYear,
  onYearChange,
  allRoundLabels,
  roundLabel,
  onRoundChange,
  sid,
}) {
  const [activeSession, setActiveSession] = useState("1교시");
  const [activeSubject, setActiveSubject] = useState("내과학1");
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfPath, setPdfPath] = useState(null);
  const [overrideTitle, setOverrideTitle] = useState(null); // ✅ 모달 제목 오버라이드
  const [highErrorQuestions, setHighErrorQuestions] = useState({});
  const [fireBySession, setFireBySession] = useState({ "1교시": [], "2교시": [], "3교시": [], "4교시": [] });
  const [loading, setLoading] = useState(false);
  const gridWrapRef = useRef(null);

  // 기본 모달 제목 (오버라이드 없을 때)
  const getModalTitle = useCallback(() => {
    if (overrideTitle) return overrideTitle;
    const formalSubject = FORMAL_SUBJECT_MAPPING[activeSubject] || activeSubject || '';
    return `${examYear}년 ${roundLabel} ${activeSession} ${formalSubject} 특별 해설`;
  }, [examYear, roundLabel, activeSession, activeSubject, overrideTitle]);

  // Functions
  const getExplanationIndex = useCallback(async (year, rLabel) => {
    try {
      const functions = getFunctions(undefined, "asia-northeast3");
      const getIndex = httpsCallable(functions, "getExplanationIndex");
      const res = await getIndex({ year, roundLabel: rLabel });
      return res.data || {};
    } catch (e) {
      console.error("해설 인덱스 조회 실패:", e);
      return {};
    }
  }, []);

  // 데이터 로드 (회차 변경 시 1회)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setOverrideTitle(null);

      if (!isRoundAvailable(roundLabel)) {
        if (!cancelled) {
          setHighErrorQuestions({});
          setFireBySession({ "1교시": [], "2교시": [], "3교시": [], "4교시": [] });
          setActiveSubject("내과학1");
          setLoading(false);
        }
        return;
      }

      try {
        const explanationIndex = await getExplanationIndex(examYear, roundLabel);

        if (!cancelled) {
          const allQuestions = {};
          const sessions = {
            "1교시": { min: 1, max: 80 },
            "2교시": { min: 1, max: 100 },
            "3교시": { min: 1, max: 80 },
            "4교시": { min: 1, max: 80 }
          };

          for (const [session, range] of Object.entries(sessions)) {
            for (let qNum = range.min; qNum <= range.max; qNum++) {
              const subject = (session === "1교시") ? "내과학1" : getSubjectByQuestion(qNum, session, roundLabel);
              (allQuestions[subject] ||= []).push({
                questionNum: qNum,
                session,
                errorRate: 0.5 // 더미(성능 위해 고정값)
              });
            }
          }

          // 최초 1회 정렬
          for (const k of Object.keys(allQuestions)) {
            allQuestions[k].sort((a, b) => a.questionNum - b.questionNum);
          }

          setHighErrorQuestions(allQuestions);
          setFireBySession({
            "1교시": explanationIndex["1교시"] || [],
            "2교시": explanationIndex["2교시"] || [],
            "3교시": explanationIndex["3교시"] || [],
            "4교시": explanationIndex["4교시"] || [],
          });

          // 기본 과목
          setActiveSubject("내과학1");
        }
      } catch (error) {
        console.error("데이터 로딩 실패:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [examYear, roundLabel, getExplanationIndex]);

  // 🔧 exp rate 조회 O(1)
  const expRateMap = useMemo(() => {
    const arr = fireBySession[activeSession] || [];
    const m = new Map();
    for (const it of arr) m.set(it.qNum, it);
    return m;
  }, [fireBySession, activeSession]);

  // 현재 과목 문항 + 정답률 결합 (메모)
  const currentQuestions = useMemo(() => {
    if (!activeSubject) return [];
    const arr = highErrorQuestions[activeSubject] || [];
    const withRate = arr.map(q => {
      const explanation = expRateMap.get(q.questionNum);
      return explanation
        ? { ...q, rate: explanation.rate, filePath: explanation.filePath, hasExp: true }
        : { ...q, rate: null, filePath: null, hasExp: false };
    });
    withRate.sort((a, b) => a.questionNum - b.questionNum);
    return withRate;
  }, [activeSubject, highErrorQuestions, expRateMap]);

  // PDF 경로 + 제목 오버라이드
  const openExplanation = (session, qNum, rate, indexedFilePath) => {
    const rNum = parseInt(String(roundLabel).replace(/\D/g, ""), 10) || 1;
    const sNum = parseInt(String(session).replace(/\D/g, ""), 10) || 1;
    const rateStr = (typeof rate === 'number' && !isNaN(rate)) ? rate.toFixed(1) : String(rate);
    const path = indexedFilePath
      || `explanation/${examYear}/${rNum}-${sNum}-${qNum}-${rateStr}.pdf`;

    // ✅ 1교시라도 실제 과목명(간/심/비/폐/신) → 정식 명칭으로 타이틀 오버라이드
    const formal = getFormalForModal(session, qNum, roundLabel);
    setOverrideTitle(`${examYear}년 ${roundLabel} ${session} ${formal} 특별 해설`);

    setPdfPath(path);
    setPdfOpen(true);
  };

  // 특정 세션의 과목 목록 (UI용)
  const getSubjectsBySessionInternal = (hq, session) => {
    if (!hq) return [];
    if (session === "1교시") return ["내과학1"];
    const subjects = [];
    for (const [subj, questions] of Object.entries(hq)) {
      if (questions.some(q => q.session === session)) subjects.push(subj);
    }
    subjects.sort((a, b) => {
      const aIndex = SUBJECT_ORDER.indexOf(a);
      const bIndex = SUBJECT_ORDER.indexOf(b);
      return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
    });
    return subjects;
  };

  const getSubjectsBySession = (session) => getSubjectsBySessionInternal(highErrorQuestions, session);

  // 세션 변경 시 과목 보정
  useEffect(() => {
    const subjects = getSubjectsBySession(activeSession);
    if (subjects.length > 0 && !subjects.includes(activeSubject)) setActiveSubject(subjects[0]);
    if (activeSession === "1교시") setActiveSubject("내과학1");
    setOverrideTitle(null); // 세션 바꾸면 이전 오버라이드 제거
  }, [activeSession, highErrorQuestions]); // eslint-disable-line

  // ====== ✨ 버튼 렌더(hover 제거 + soft wrap: flex-wrap) ======
  const renderButtons = () => {
    if (!currentQuestions.length) return null;

    // 기기폭에 맞춰 버튼 크기/간격만 간단 조정
    const isMobile = typeof window !== "undefined" && window.innerWidth < 600;
    const isTablet = typeof window !== "undefined" && window.innerWidth >= 600 && window.innerWidth < 900;

    const cellW = isMobile ? 45 : isTablet ? 55 : 60;
    const cellH = cellW; // 정사각형
    const gap = isMobile ? 2 : 3;

    return (
      <div
        ref={gridWrapRef}
        className="btn-grid"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: `${gap}px`,
          justifyContent: 'center',
          alignContent: 'flex-start',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {currentQuestions.map((q) => {
          const qNum = q.questionNum;
          const session = q.session;
          const hasExp = q.hasExp;
          const rate = q.rate;
          const filePath = q.filePath;
          const numericRate = (typeof rate === 'number' && !isNaN(rate)) ? rate : Number(rate);

          let styleMods;
          if (hasExp) {
            const clampedRate = Math.min(100, Math.max(0, numericRate));
            const difficulty = 100 - clampedRate;
            const hue = 0;
            const bgSaturation = difficulty >= 50 ? Math.min(100, 40 + (difficulty - 50) * 1.5) : Math.max(20, 20 + difficulty * 0.4);
            const bgLightness = difficulty >= 50 ? Math.min(50, 28 + (difficulty - 50) * 0.44) : Math.max(20, 20 + difficulty * 0.16);
            const borderColor = `hsl(${hue}, ${bgSaturation}%, ${Math.min(60, bgLightness + 15)}%)`;

            styleMods = {
              color: 'hsl(0, 0%, 95%)',
              borderColor,
              borderWidth: '2px',
              borderStyle: 'solid',
              background: `hsl(${hue}, ${bgSaturation}%, ${bgLightness}%)`,
              boxShadow: 'none',          // 🔥 hover 효과 제거
              cursor: 'pointer',
              fontWeight: 700,
            };
          } else {
            styleMods = {
              color: 'hsl(0, 0%, 60%)',
              borderColor: 'var(--line)',
              background: 'rgba(255,255,255,0.03)',
              opacity: 0.6,
              cursor: 'default',
              boxShadow: 'none',
            };
          }

          const label = hasExp
            ? `문항 ${qNum} · 정답률 ${numericRate.toFixed(1)}% · 특별 해설`
            : `문항 ${qNum} (해설 없음)`;

          return (
            <button
              key={`${session}-${qNum}`}
              type="button"
              title={label}
              aria-label={label}
              onClick={hasExp ? () => openExplanation(session, qNum, rate, filePath) : undefined}
              disabled={!hasExp}
              className={`qbtn ${hasExp ? 'qbtn-rate' : 'no-explanation'}`}
              style={{
                width: `${cellW}px`,
                height: `${cellH}px`,
                fontSize: `${Math.max(8, Math.min(12, cellW / 5))}px`,
                position: 'relative',
                fontWeight: 700,
                transition: 'none',     // 🔥 hover/transition 제거
                minWidth: 0,
                minHeight: 0,
                boxSizing: 'border-box',
                ...styleMods
              }}
            >
              {qNum}
              {hasExp && (
                <span
                  style={{
                    position: 'absolute',
                    bottom: '2px',
                    fontSize: `${Math.max(9, Math.min(11, cellW / 6))}px`,
                    fontWeight: 700,
                    color: 'hsl(0, 0%, 90%)',
                    opacity: 1,
                    lineHeight: 1,
                    textShadow: 'none'
                  }}
                >
                  {`${numericRate.toFixed(1)}%`}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="wrong-panel-root">
      <div className="round-tabs year-tabs" role="tablist" aria-label="연도 선택">
        {examYears.map((year) => (
          <button
            key={year}
            role="tab"
            aria-selected={examYear === year}
            className={`tab-btn ${examYear === year ? "active" : ""}`}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onYearChange(year);
            }}
          >
            {year}
          </button>
        ))}
      </div>

      {/* 회차 탭 */}
      <div className="round-tabs" role="tablist" aria-label="회차 선택">
        {allRoundLabels.map((r) => {
          const isAvailable = isRoundAvailable(r);
          return (
            <button
              key={r}
              role="tab"
              aria-selected={roundLabel === r}
              className={`tab-btn ${roundLabel === r ? "active" : ""}`}
              type="button"
              disabled={!isAvailable}
              onClick={(e) => { e.stopPropagation(); if (isAvailable) onRoundChange(r); }}
            >
              {r}
            </button>
          );
        })}
      </div>

      {/* 교시 탭 */}
      <div className="session-tabs" role="tablist" aria-label="교시 선택">
        {SESSIONS.map((s) => {
          const isAvailable = isSessionAvailable(roundLabel, s);
          return (
            <button
              key={s}
              role="tab"
              aria-selected={activeSession === s}
              className={`tab-btn ${activeSession === s ? "active" : ""}`}
              type="button"
              disabled={!isAvailable}
              onClick={(e) => {
                e.stopPropagation();
                if (isAvailable) {
                  setActiveSession(s);
                  if (s === "1교시") setActiveSubject("내과학1");
                }
              }}
            >
              {s}
            </button>
          );
        })}
      </div>

      {/* 과목 탭 */}
      {activeSession === "1교시" ? (
        <div className="subject-tabs" role="tablist" aria-label="과목 선택">
          <button
            key="내과학1"
            role="tab"
            aria-selected={true}
            className="tab-btn active"
            type="button"
            onClick={(e) => { e.stopPropagation(); setActiveSubject("내과학1"); }}
          >
            내과학1
          </button>
        </div>
      ) : (
        getSubjectsBySession(activeSession).length > 0 && (
          <div className="subject-tabs" role="tablist" aria-label="과목 선택">
            {getSubjectsBySession(activeSession).map((s) => (
              <button
                key={s}
                role="tab"
                aria-selected={activeSubject === s}
                className={`tab-btn ${activeSubject === s ? "active" : ""}`}
                type="button"
                onClick={(e) => { e.stopPropagation(); setActiveSubject(s); }}
              >
                {s}
              </button>
            ))}
          </div>
        )
      )}

      {/* 콘텐츠 */}
      <div className="tab-content">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <div className="loading-text">문항 데이터를 불러오고 있습니다...</div>
          </div>
        ) : (
          renderButtons()
        )}
      </div>

      <PdfModalPdfjs
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        filePath={pdfPath}
        sid={sid}
        title={getModalTitle()}
      />
    </div>
  );
}
